
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search, Filter, ArrowDownWideNarrow } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface JournalHeaderProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  onFilterChange?: (filter: string) => void;
  onSortChange?: (sort: string) => void;
  onNewEntry?: () => void;
}

export const JournalHeader = ({ 
  searchTerm = "", 
  onSearchChange, 
  onFilterChange,
  onSortChange,
  onNewEntry 
}: JournalHeaderProps) => {
  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary/80 to-primary bg-clip-text text-transparent">
              Mon journal de santé
            </h1>
            <p className="text-muted-foreground max-w-2xl text-sm">
              Suivez votre santé au quotidien, notez vos observations et visualisez votre progression dans le temps.
            </p>
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              placeholder="Rechercher dans le journal..."
              className="pl-10 bg-background/50 backdrop-blur-sm border-muted/50"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Filter className="h-4 w-4" /> Filtres
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onFilterChange?.("all")}>
                  Toutes les entrées
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onFilterChange?.("thisWeek")}>
                  Cette semaine
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onFilterChange?.("thisMonth")}>
                  Ce mois
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => onFilterChange?.("withSymptoms")}>
                  Avec symptômes
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <ArrowDownWideNarrow className="h-4 w-4" /> Trier
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => onSortChange?.("newest")}>
                  Plus récent
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onSortChange?.("oldest")}>
                  Plus ancien
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button 
                  className="bg-primary hover:bg-primary/90 shadow-sm"
                  onClick={onNewEntry}
                >
                  <Plus className="mr-1 h-4 w-4" /> 
                  Nouvelle entrée
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Ajouter une nouvelle entrée dans votre journal</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
